import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, LogOut, Users, Eye, Search, Activity, Mail, TrendingUp, Lock, Plane, RefreshCw, CheckCircle2, XCircle, Clock, ExternalLink, Globe, AlertTriangle, RotateCcw, Ban, MousePointerClick } from "lucide-react";
import { buildSearchHref } from "@/lib/search-href";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Header, Footer } from "@/components/layout";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { airportByLowerCode } from "@/lib/seo-routes";
import { flagEmoji, classifyAgent, primaryLanguage, languageName, deviceInfo, deviceKindLabel, referrerInfo } from "@/lib/admin-ua";
import { AdminGeoMap } from "@/components/admin-geo-map";
import { AIRPORTS } from "@/lib/airports";

// Hub IATA → kod podpowiadany userowi (metro jeśli istnieje, np. WAW → WAR).
const HUB_DISPLAY_CODE: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const a of AIRPORTS) {
    if (a.isMetro && a.members) for (const m of a.members) if (!out[m]) out[m] = a.code;
  }
  return out;
})();
const hubDisplay = (hub: string) => HUB_DISPLAY_CODE[hub] ?? hub;

export const Route = createFileRoute("/admin/")({
  head: () => ({ meta: [
    { title: "Panel administracyjny — Tanie Latanie" },
    { name: "description", content: "Panel administracyjny Tanie Latanie: statystyki wyszukiwania, ceny i wiadomości." },
    { name: "robots", content: "noindex" },
    { property: "og:title", content: "Panel administracyjny — Tanie Latanie" },
    { property: "og:description", content: "Panel administracyjny Tanie Latanie: statystyki wyszukiwania, ceny i wiadomości." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  validateSearch: (search: Record<string, unknown>) => ({
    from: search.from === "login" ? "login" as const : undefined,
  }),
  component: AdminPage,
});

type Msg = { id: string; name: string; email: string; message: string; read: boolean; created_at: string };
type Event = { id: number; session_id: string; path: string; referrer: string | null; utm_source: string | null; utm_medium: string | null; utm_campaign: string | null; utm_term?: string | null; user_agent: string | null; ip: string | null; country: string | null; accept_language: string | null; created_at: string };
type SearchQ = { id: number; session_id: string | null; from_iata: string | null; to_iata: string | null; depart_date: string | null; return_date: string | null; pax: number | null; trip_type: string | null; service_class: string | null; direct_only: boolean | null; created_at: string };
type PriceRow = { from_iata: string; to_iata: string; real_min_price: number; display_price: number; checked_at: string };
type CronRun = { jobname: string; status: string; start_time: string; end_time: string | null; duration_s: number | null; return_message: string };
type CronJob = { jobname: string; schedule: string; active: boolean };
type PriceStats = {
  summary: { total: number; oldest_checked_at: string | null; newest_checked_at: string | null; avg_real: number | null; avg_display: number | null; min_display: number | null; max_display: number | null; stale_24h: number; stale_48h: number };
  oldest: PriceRow[];
  newest: PriceRow[];
  runs: CronRun[];
  jobs: CronJob[];
};

const EVENT_COLUMNS = "id,session_id,path,referrer,utm_source,utm_medium,utm_campaign,utm_term,user_agent,ip,country,accept_language,created_at";
const SEARCH_COLUMNS = "id,session_id,from_iata,to_iata,depart_date,return_date,pax,trip_type,service_class,direct_only,created_at";
const MESSAGE_COLUMNS = "id,name,email,message,read,created_at";

const ADMIN_ANALYTICS_DAYS = 30;
// Twardy cap na initial fetch — głębokie OFFSET-y po analytics_events były
// odpowiedzialne za ~98% obciążenia bazy (jedna otwarta karta admina = ~200k
// zapytań/dobę × ~800ms każde). Tail-poll + realtime dociągają nowe wpisy,
// więc startowo wystarczy ostatnich kilka tysięcy najświeższych rekordów.
const ADMIN_INITIAL_LIMIT = 1000;
const ADMIN_OUTBOUND_INITIAL_LIMIT = 500;
const ADMIN_SEARCHES_INITIAL_LIMIT = 500;
const DAY_MS = 86_400_000;

const adminSinceIso = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (ADMIN_ANALYTICS_DAYS - 1));
  return start.toISOString();
};

function keepAdminWindow<T extends { created_at: string }>(rows: T[]): T[] {
  const since = Date.now() - ADMIN_ANALYTICS_DAYS * DAY_MS;
  return rows.filter((row) => new Date(row.created_at).getTime() >= since);
}

async function fetchRecentAnalyticsEvents(sinceIso: string): Promise<Event[]> {
  const { data, error } = await supabase
    .from("analytics_events")
    .select(EVENT_COLUMNS)
    .gte("created_at", sinceIso)
    .order("created_at", { ascending: false })
    .limit(ADMIN_INITIAL_LIMIT);
  if (error) throw error;
  return (data ?? []) as Event[];
}

async function fetchRecentOutboundEvents(sinceIso: string): Promise<Event[]> {
  const { data, error } = await supabase
    .from("analytics_events")
    .select(EVENT_COLUMNS)
    .eq("path", "/_event/search-submit")
    .gte("created_at", sinceIso)
    .order("created_at", { ascending: false })
    .limit(ADMIN_OUTBOUND_INITIAL_LIMIT);
  if (error) throw error;
  return (data ?? []) as Event[];
}

// Granica „dnia statystycznego" dla licznika kliknięć Szukaj: 5:00 czasu
// warszawskiego. daysAgo=0 → początek „dziś", daysAgo=1 → początek „wczoraj".
// Zwraca instant (Date w UTC) odpowiadający 05:00 Europe/Warsaw tego dnia.
function warsawStatsDayStart(daysAgo: number): Date {
  const tz = "Europe/Warsaw";
  const wallNow = new Date(new Date().toLocaleString("en-US", { timeZone: tz }));
  const shifted = new Date(wallNow.getTime() - 5 * 3_600_000);
  shifted.setDate(shifted.getDate() - daysAgo);
  const guess = Date.UTC(shifted.getFullYear(), shifted.getMonth(), shifted.getDate(), 5, 0, 0);
  const wallAtGuess = new Date(new Date(guess).toLocaleString("en-US", { timeZone: tz }));
  return new Date(guess - (wallAtGuess.getTime() - guess));
}

async function fetchRecentSearches(sinceIso: string): Promise<SearchQ[]> {
  const { data, error } = await supabase
    .from("search_queries")
    .select(SEARCH_COLUMNS)
    .gte("created_at", sinceIso)
    .order("created_at", { ascending: false })
    .limit(ADMIN_SEARCHES_INITIAL_LIMIT);
  if (error) throw error;
  return (data ?? []) as SearchQ[];
}

function AdminPage() {
  const nav = useNavigate();
  const search = Route.useSearch();
  useEffect(() => {
    if (search.from === "login") {
      toast.info("Adres logowania zmienił się na /admin. Zaktualizuj swoje zakładki.");
      nav({ to: "/admin", search: {} as never, replace: true });
    }
  }, [search.from, nav]);
  const [authChecked, setAuthChecked] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [outboundEvents, setOutboundEvents] = useState<Event[]>([]);
  const [searches, setSearches] = useState<SearchQ[]>([]);
  const [priceStats, setPriceStats] = useState<PriceStats | null>(null);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [tick, setTick] = useState(0);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [submitClicks, setSubmitClicks] = useState<{ today: number; yesterday: number } | null>(null);

  const checkAuth = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      setHasSession(false);
      setIsAdmin(false);
      setAuthChecked(true);
      return;
    }
    setHasSession(true);
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", data.session.user.id);
    const admin = !!roles?.some((r) => r.role === "admin");
    setIsAdmin(admin);
    setAuthChecked(true);
    if (!admin) toast.error("Brak uprawnień admina");
  }, []);

  // auth + admin gate
  useEffect(() => {
    checkAuth();
    const { data: sub } = supabase.auth.onAuthStateChange(() => { checkAuth(); });
    return () => { sub.subscription.unsubscribe(); };
  }, [checkAuth]);

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword });
    setLoggingIn(false);
    if (error) { toast.error("Niepoprawne dane logowania"); return; }
  };

  const loadAll = useCallback(async () => {
    const sinceIso = adminSinceIso();
    const [m, e, s, o] = await Promise.all([
      supabase.from("contact_messages").select(MESSAGE_COLUMNS).order("created_at", { ascending: false }).limit(500),
      fetchRecentAnalyticsEvents(sinceIso),
      fetchRecentSearches(sinceIso),
      fetchRecentOutboundEvents(sinceIso),
    ]);
    if (m.data) setMsgs(m.data as Msg[]);
    // Mergujemy zamiast nadpisywać — paginowany refetch potrafi „zgubić"
    // pojedyncze rekordy (race z realtime/tail-poll: nowy INSERT podczas
    // pobierania kolejnych stron przesuwa `range`, więc ten sam wiersz
    // wypadnie z wyniku). Bez merge'a sesje z „Live ścieżek" znikały
    // co 60s na ~5-10s do następnego ticka — wizualne mruganie.
    const mergeById = (incoming: Event[]) => (prev: Event[]) => {
      const map = new Map<number, Event>();
      for (const row of incoming) map.set(row.id, row);
      for (const row of prev) if (!map.has(row.id)) map.set(row.id, row);
      return keepAdminWindow([...map.values()].sort(
        (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
      ));
    };
    setEvents(mergeById(e));
    setOutboundEvents(mergeById(o));
    setSearches((prev) => {
      const map = new Map<number, SearchQ>();
      for (const row of s) map.set(row.id, row);
      for (const row of prev) if (!map.has(row.id)) map.set(row.id, row);
      return keepAdminWindow([...map.values()].sort(
        (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
      ));
    });
  }, []);

  // Licznik kliknięć „Szukaj" (widget Travelpayouts): dziś / wczoraj,
  // dzień liczony od 5:00 czasu warszawskiego. Liczymy COUNT po stronie bazy,
  // bo lokalna lista eventów jest ucinana (ADMIN_INITIAL_LIMIT).
  const loadSubmitClicks = useCallback(async () => {
    const todayStart = warsawStatsDayStart(0).toISOString();
    const yesterdayStart = warsawStatsDayStart(1).toISOString();
    const [t, y] = await Promise.all([
      supabase.from("analytics_events").select("id", { count: "exact", head: true }).eq("path", "/_event/search-submit").gte("created_at", todayStart),
      supabase.from("analytics_events").select("id", { count: "exact", head: true }).eq("path", "/_event/search-submit").gte("created_at", yesterdayStart).lt("created_at", todayStart),
    ]);
    setSubmitClicks({ today: t.count ?? 0, yesterday: y.count ?? 0 });
  }, []);

  const loadPriceStats = useCallback(async () => {
    setPricesLoading(true);
    const { data, error } = await supabase.rpc("get_route_prices_admin_stats");
    setPricesLoading(false);
    if (error) {
      toast.error("Nie udało się pobrać statystyk cen");
      return;
    }
    setPriceStats(data as PriceStats);
  }, []);

  const triggerRefresh = async (offset: number) => {
    setRefreshingPrices(true);
    try {
      const r = await fetch(`/api/public/hooks/refresh-route-prices?limit=10&offset=${offset}`, { method: "POST" });
      const j = (await r.json()) as { ok?: number; processed?: number };
      toast.success(`Odświeżono ${j.ok ?? 0}/${j.processed ?? 0} par (offset ${offset})`);
      await loadPriceStats();
    } catch {
      toast.error("Nie udało się uruchomić odświeżania");
    } finally {
      setRefreshingPrices(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadAll();
    loadPriceStats();
    loadSubmitClicks();

    // Pauza, gdy karta admina jest w tle — to był główny powód „skoków"
    // zużycia bazy: zostawiona otwarta karta przez kilka dni generowała
    // setki tysięcy odpytań analytics_events na dobę. Browsery i tak
    // throttlują setInterval w tle, ale lepiej jawnie nie odpytywać.
    const isHidden = () => typeof document !== "undefined" && document.hidden;
    const guarded = (fn: () => void) => () => { if (!isHidden()) fn(); };

    const interval = setInterval(guarded(() => { loadAll(); loadSubmitClicks(); setTick((t) => t + 1); }), 900_000);
    const priceInterval = setInterval(guarded(() => { loadPriceStats(); }), 600_000);
    const liveTick = setInterval(() => setTick((t) => t + 1), 5_000);

    // Tail-poll: realtime postgres_changes potrafi zgubić pojedynczy INSERT
    // (np. serię /_state/search w ciągu 1-2s) — przez to admin widział
    // „Szuka… 0 wyników" mimo, że user już dawno miał wyniki. Co 30s
    // ciągniemy lekki ogon (ostatnie 5 min) i mergujemy po id. Pauza
    // gdy karta w tle.
    const tailPoll = setInterval(guarded(async () => {
      try {
        const since = new Date(Date.now() - 5 * 60_000).toISOString();
        const { data, error } = await supabase
          .from("analytics_events")
          .select(EVENT_COLUMNS)
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(500);
        if (error || !data) return;
        const rows = data as Event[];
        setEvents((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          const fresh = rows.filter((r) => !seen.has(r.id));
          if (fresh.length === 0) return prev;
          return keepAdminWindow([...fresh, ...prev]);
        });
        const outbound = rows.filter((r) => r.path === "/_event/search-submit");
        if (outbound.length > 0) {
          setOutboundEvents((prev) => {
            const seen = new Set(prev.map((e) => e.id));
            const fresh = outbound.filter((r) => !seen.has(r.id));
            if (fresh.length === 0) return prev;
            return keepAdminWindow([...fresh, ...prev]);
          });
        }
      } catch {
        // ignore — następny tick spróbuje znowu
      }
    }), 30_000);

    // Po powrocie do karty — natychmiast odśwież, żeby dane nie były nieaktualne.
    const onVisible = () => { if (!document.hidden) { loadAll(); } };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);

    const channel = supabase
      .channel("admin-live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "analytics_events" }, (p) => {
        const ev = p.new as Event;
        setEvents((prev) => prev.some((e) => e.id === ev.id) ? prev : keepAdminWindow([ev, ...prev]));
        if (ev.path === "/_event/search-submit") {
          setOutboundEvents((prev) => prev.some((e) => e.id === ev.id) ? prev : keepAdminWindow([ev, ...prev]));
        }
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "contact_messages" }, (p) => {
        setMsgs((prev) => [p.new as Msg, ...prev]);
        toast.info(`Nowa wiadomość od ${(p.new as Msg).name}`);
      })
      .subscribe();
    return () => {
      clearInterval(interval);
      clearInterval(priceInterval);
      clearInterval(liveTick);
      clearInterval(tailPoll);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
      supabase.removeChannel(channel);
    };
  }, [isAdmin, loadAll, loadPriceStats, loadSubmitClicks]);

  const deleteMsg = async (id: string) => {
    const { error } = await supabase.from("contact_messages").delete().eq("id", id);
    if (error) { toast.error("Nie udało się usunąć"); return; }
    setMsgs((prev) => prev.filter((m) => m.id !== id));
    toast.success("Usunięto");
  };

  const markRead = async (id: string) => {
    await supabase.from("contact_messages").update({ read: true }).eq("id", id);
    setMsgs((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
  };

  const logout = async () => { await supabase.auth.signOut(); nav({ to: "/" }); };

  // Sesje botów (Googlebot, Bingbot, GPTBot, headless, agenci Lovable itp.)
  // — wykluczamy je ze WSZYSTKICH statystyk i z „Live ścieżek". Sesja jest
  // uznana za botową, jeśli choć jeden jej event ma UA klasyfikowane jako
  // bot / headless / lovable.
  const isLovableRef = (ref: string | null | undefined) => {
    if (!ref) return false;
    try {
      const h = new URL(ref).hostname.toLowerCase();
      return h.endsWith(".lovable.app") || h.endsWith(".lovable.dev") || h === "lovable.app" || h === "lovable.dev";
    } catch {
      return false;
    }
  };
  const botSessions = useMemo(() => {
    const set = new Set<string>();
    for (const e of events) {
      if (e.user_agent) {
        const k = classifyAgent(e.user_agent).kind;
        if (k === "bot" || k === "headless" || k === "lovable") { set.add(e.session_id); continue; }
      }
      if (isLovableRef(e.referrer)) set.add(e.session_id);
    }
    return set;
  }, [events]);

  const humanEvents = useMemo(
    () => events.filter((e) => !botSessions.has(e.session_id)),
    [events, botSessions],
  );
  // Kliknięcia „Szukaj" są ładowane osobnym, paginowanym zapytaniem za całe
  // 30 dni, żeby nie wypadały z list/statystyk przy dużym ruchu. Filtrujemy
  // boty per-event (po UA + referrer) — bo sesja może nie mieć pełnej historii.
  const humanOutboundEvents = useMemo(
    () =>
      outboundEvents.filter((e) => {
        if (botSessions.has(e.session_id)) return false;
        if (e.user_agent) {
          const k = classifyAgent(e.user_agent).kind;
          if (k === "bot" || k === "headless" || k === "lovable") return false;
        }
        if (isLovableRef(e.referrer)) return false;
        return true;
      }),
    [outboundEvents, botSessions],
  );
  const humanSearches = useMemo(
    () => searches.filter((s) => !s.session_id || !botSessions.has(s.session_id)),
    [searches, botSessions],
  );

  // ===== stats =====
  const stats = useMemo(() => {
    const now = Date.now();
    const day = 86_400_000;
    const publicEvents = humanEvents.filter((e) => !e.path.startsWith("/admin") && !e.path.startsWith("/out/") && !e.path.startsWith("/_state/") && !e.path.startsWith("/_event/"));
    // Wyjścia liczymy z osobnego, pełnego strumienia 30 dni.
    const last24 = publicEvents.filter((e) => now - new Date(e.created_at).getTime() < day);
    const last7 = publicEvents.filter((e) => now - new Date(e.created_at).getTime() < 7 * day);
    const out24 = humanOutboundEvents.filter((e) => now - new Date(e.created_at).getTime() < day).length;


    // online: unikalne sesje z dowolnym sygnałem aktywności w ostatnich 90 s.
    // UWAGA: bierzemy `humanEvents` (nie `publicEvents`), żeby uwzględnić
    // heartbeaty `/_state/*` i `/_event/*` — user czytający stronę nie
    // generuje nowych pageviews, ale wysyła state'y (theme, search) i bez
    // tego znikał z licznika po 60 s, choć w „Live ścieżki" wciąż świecił
    // się na zielono (próg 90 s). Wykluczamy tylko ruch /admin.
    const online = new Set(
      humanEvents
        .filter((e) => !e.path.startsWith("/admin") && now - new Date(e.created_at).getTime() < 90_000)
        .map((e) => e.session_id),
    );

    // top paths
    const pathCounts = new Map<string, number>();
    last7.forEach((e) => pathCounts.set(e.path, (pathCounts.get(e.path) ?? 0) + 1));
    const topPaths = [...pathCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);

    // referrers + utm
    const refCounts = new Map<string, number>();
    last7.forEach((e) => {
      let label = "(bezpośrednie)";
      if (e.utm_source) label = `utm:${e.utm_source}`;
      else if (e.referrer) {
        try { label = new URL(e.referrer).hostname; } catch { label = e.referrer; }
      }
      refCounts.set(label, (refCounts.get(label) ?? 0) + 1);
    });
    const topRefs = [...refCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);

    // unique sessions
    const uniq24 = new Set(last24.map((e) => e.session_id)).size;
    const uniq7 = new Set(last7.map((e) => e.session_id)).size;

    return { online: online.size, total: humanEvents.length, last24: last24.length, last7: last7.length, uniq24, uniq7, topPaths, topRefs, out24 };

  }, [humanEvents, humanOutboundEvents, humanSearches, tick]);

  // ===== „Gdziekolwiek" — intencje i kliknięcia w kierunki =====
  // Eventy zapisane przez trackAnywhereIntent / trackAnywhereClick mają ścieżki
  // `/_event/anywhere-intent` i `/_event/anywhere-click`, a payload (source,
  // query, origin, destination, country, price) siedzi jako JSON w utm_campaign.
  const anywhereStats = useMemo(() => {
    const now = Date.now();
    const day = 86_400_000;
    type Intent = { source?: string; query?: string; origin?: string };
    type Click = { origin?: string; destination?: string; country?: string; price?: number };
    const intents24: Array<{ at: string; session: string; data: Intent }> = [];
    const clicks24: Array<{ at: string; session: string; data: Click }> = [];
    for (const e of humanEvents) {
      const age = now - new Date(e.created_at).getTime();
      if (age > day) continue;
      if (e.path === "/_event/anywhere-intent" && e.utm_campaign) {
        try { intents24.push({ at: e.created_at, session: e.session_id, data: JSON.parse(e.utm_campaign) }); } catch {}
      } else if (e.path === "/_event/anywhere-click" && e.utm_campaign) {
        try { clicks24.push({ at: e.created_at, session: e.session_id, data: JSON.parse(e.utm_campaign) }); } catch {}
      }
    }
    const bySource = new Map<string, number>();
    intents24.forEach((i) => bySource.set(i.data.source ?? "?", (bySource.get(i.data.source ?? "?") ?? 0) + 1));
    const byDestination = new Map<string, { country?: string; n: number; minPrice?: number }>();
    clicks24.forEach((c) => {
      const key = c.data.destination ?? "?";
      const cur = byDestination.get(key) ?? { country: c.data.country, n: 0, minPrice: undefined };
      cur.n += 1;
      if (c.data.price && (!cur.minPrice || c.data.price < cur.minPrice)) cur.minPrice = c.data.price;
      if (!cur.country && c.data.country) cur.country = c.data.country;
      byDestination.set(key, cur);
    });
    const topDestinations = [...byDestination.entries()].sort((a, b) => b[1].n - a[1].n).slice(0, 10);
    const topQueries = [...intents24.filter((i) => i.data.query).reduce((m, i) => {
      const q = i.data.query!.toLowerCase();
      m.set(q, (m.get(q) ?? 0) + 1);
      return m;
    }, new Map<string, number>())].sort((a, b) => b[1] - a[1]).slice(0, 8);
    const recent = [...intents24.slice(-15)].reverse();
    return {
      intents: intents24.length,
      clicks: clicks24.length,
      ctr: intents24.length ? Math.round((clicks24.length / intents24.length) * 100) : 0,
      bySource: [...bySource.entries()].sort((a, b) => b[1] - a[1]),
      topDestinations,
      topQueries,
      recent,
    };
  }, [humanEvents, tick]);

  // mapa session_id -> ostatnie wyszukiwanie (z search_queries) — używane
  // w „Live ścieżki" do pokazania, jakiego lotu szuka dana osoba.
  const lastSearchBySession = useMemo(() => {
    const map = new Map<string, SearchQ>();
    for (const s of humanSearches) {
      const sid = s.session_id ?? "";
      if (!sid) continue;
      if (!map.has(sid)) map.set(sid, s);
    }
    return map;
  }, [humanSearches]);

  // Posortowane (rosnąco) timestampy wszystkich eventów per session_id —
  // używane w „Live ścieżki" do pokazania, czy user jest nowy, czy
  // powracający (i kiedy był ostatnio).
  const sessionTimes = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const e of events) {
      const t = +new Date(e.created_at);
      const arr = map.get(e.session_id) ?? [];
      arr.push(t);
      map.set(e.session_id, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a - b);
    return map;
  }, [events]);

  // live sessions
  const liveSessions = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, Event[]>();
    humanEvents.filter((e) => !e.path.startsWith("/admin") && !e.path.startsWith("/_state/") && now - new Date(e.created_at).getTime() < 15 * 60_000).forEach((e) => {
      const arr = map.get(e.session_id) ?? [];
      arr.push(e);
      map.set(e.session_id, arr);
    });
    return [...map.entries()]
      .map(([sid, evs]) => ({ sid, evs: evs.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)) }))
      .sort((a, b) => +new Date(b.evs[0].created_at) - +new Date(a.evs[0].created_at));
  }, [humanEvents, tick]);

  // Najnowszy „search state" per sesja: ile wyników widzi user, czy nadal
  // szukamy, czy padło z błędem. Eventy `/_state/search` pushuje search.tsx
  // przy każdej zmianie stanu wyszukiwarki (throttling po stronie klienta).
  // Używamy `events` (bez filtru bot — boty i tak nie wchodzą na /search).
  type SearchLiveState = {
    visible: number;
    status: "loading" | "results" | "empty" | "error";
    route?: string;
    pairs?: number;
    errorMsg?: string | null;
    partial?: string | null;
    truncated?: string | null;
    at: number;
  };
  const searchStateBySession = useMemo(() => {
    const map = new Map<string, SearchLiveState>();
    for (const e of events) {
      if (e.path !== "/_state/search" || !e.utm_campaign) continue;
      const sid = e.session_id;
      const at = +new Date(e.created_at);
      const prev = map.get(sid);
      if (prev && prev.at >= at) continue;
      try {
        const parsed = JSON.parse(e.utm_campaign) as Omit<SearchLiveState, "at">;
        map.set(sid, { ...parsed, at });
      } catch {
        // ignore malformed payload
      }
    }
    return map;
  }, [events]);

  // Sugestia huba wylotu, którą user dostał na start (na podstawie IP/CF).
  // Logowana raz, przy pierwszej "prawdziwej" odsłonie strony przez
  // `logPageview`. Tu bierzemy najwcześniejszy wpis per sesja — to jest
  // "co user zobaczył na początku".
  type GeoOriginState = { hub: string; reason: string; city: string | null; country: string | null; at: number };
  const geoOriginBySession = useMemo(() => {
    const map = new Map<string, GeoOriginState>();
    for (const e of events) {
      if (e.path !== "/_state/geo-origin" || !e.utm_campaign) continue;
      const sid = e.session_id;
      const at = +new Date(e.created_at);
      const prev = map.get(sid);
      // bierzemy NAJSTARSZY (pierwsza odsłona = to co user zobaczył na start)
      if (prev && prev.at <= at) continue;
      try {
        const parsed = JSON.parse(e.utm_campaign) as Omit<GeoOriginState, "at">;
        map.set(sid, { ...parsed, at });
      } catch {
        // ignore
      }
    }
    return map;
  }, [events]);

  // Motyw (dark/light) per sesja — pushowany z `useTheme` na hydratacji
  // i przy każdej zmianie. Bierzemy NAJNOWSZY event (user mógł przełączyć
  // w trakcie wizyty), żeby badge w live pokazywał aktualny stan.
  const themeBySession = useMemo(() => {
    const map = new Map<string, { theme: "dark" | "light"; at: number }>();
    for (const e of events) {
      if (e.path !== "/_state/theme" || !e.utm_campaign) continue;
      const sid = e.session_id;
      const at = +new Date(e.created_at);
      const prev = map.get(sid);
      if (prev && prev.at >= at) continue;
      try {
        const parsed = JSON.parse(e.utm_campaign) as { theme?: string };
        if (parsed.theme === "dark" || parsed.theme === "light") {
          map.set(sid, { theme: parsed.theme, at });
        }
      } catch {
        // ignore
      }
    }
    return map;
  }, [events]);

  // ===== Monitoring zdrowia wyszukiwarki =====
  // Liczymy eventy `/_event/search-cancel` i `/_event/search-restart` w dwóch
  // oknach: ostatnie 5 min (alert real-time) i 60 min (kontekst trendu).
  // Progi alertów ustawiamy luźno — chcemy wychwycić ANOMALIE, nie szum
  // pojedynczych userów. Każdy alert ma badge w nagłówku + szczegóły w karcie.
  const searchHealth = useMemo(() => {
    const now = Date.now();
    const W5 = 5 * 60_000;
    const W60 = 60 * 60_000;
    type Bucket = {
      cancels5: number; cancels60: number;
      restarts5: number; restarts60: number;
      byReason5: Map<string, number>;
      byRoute5: Map<string, { cancels: number; restarts: number }>;
      recent: Array<{ at: string; kind: "cancel" | "restart"; payload: Record<string, unknown>; session: string }>;
    };
    const b: Bucket = {
      cancels5: 0, cancels60: 0, restarts5: 0, restarts60: 0,
      byReason5: new Map(), byRoute5: new Map(), recent: [],
    };
    for (const e of events) {
      const isCancel = e.path === "/_event/search-cancel";
      const isRestart = e.path === "/_event/search-restart";
      if (!isCancel && !isRestart) continue;
      const age = now - new Date(e.created_at).getTime();
      if (age > W60) continue;
      let parsed: Record<string, unknown> = {};
      try { parsed = e.utm_campaign ? JSON.parse(e.utm_campaign) : {}; } catch { /* ignore */ }
      const route = typeof parsed.route === "string" ? parsed.route : "?";
      if (isCancel) {
        b.cancels60++;
        if (age <= W5) {
          b.cancels5++;
          const reason = typeof parsed.reason === "string" ? parsed.reason : "?";
          b.byReason5.set(reason, (b.byReason5.get(reason) ?? 0) + 1);
          const cur = b.byRoute5.get(route) ?? { cancels: 0, restarts: 0 };
          cur.cancels++;
          b.byRoute5.set(route, cur);
        }
      } else {
        b.restarts60++;
        if (age <= W5) {
          b.restarts5++;
          const cur = b.byRoute5.get(route) ?? { cancels: 0, restarts: 0 };
          cur.restarts++;
          b.byRoute5.set(route, cur);
        }
      }
      if (age <= W5) {
        b.recent.push({
          at: e.created_at,
          kind: isCancel ? "cancel" : "restart",
          payload: parsed,
          session: e.session_id,
        });
      }
    }
    b.recent.sort((a, b2) => +new Date(b2.at) - +new Date(a.at));
    // Progi: alarmujemy gdy w 5 min mamy >20 anulowań ALBO >10 restartów
    // ALBO którakolwiek pojedyncza trasa zebrała >5 restartów (pętla F5).
    const hotRoutes = [...b.byRoute5.entries()]
      .filter(([, v]) => v.restarts >= 5 || v.cancels >= 10)
      .sort((a, b2) => (b2[1].restarts + b2[1].cancels) - (a[1].restarts + a[1].cancels));
    const alert =
      b.cancels5 > 20 || b.restarts5 > 10 || hotRoutes.length > 0
        ? {
            level: (b.cancels5 > 40 || b.restarts5 > 20) ? "critical" as const : "warning" as const,
            reasons: [
              b.cancels5 > 20 ? `${b.cancels5} anulowań w 5 min` : null,
              b.restarts5 > 10 ? `${b.restarts5} restartów w 5 min` : null,
              hotRoutes.length > 0 ? `${hotRoutes.length} trasa(y) w pętli` : null,
            ].filter(Boolean) as string[],
          }
        : null;
    return {
      cancels5: b.cancels5, cancels60: b.cancels60,
      restarts5: b.restarts5, restarts60: b.restarts60,
      byReason5: [...b.byReason5.entries()].sort((a, b2) => b2[1] - a[1]),
      hotRoutes: hotRoutes.slice(0, 8),
      recent: b.recent.slice(0, 20),
      alert,
    };
  }, [events, tick]);





  const topSearches = useMemo(() => {
    const c = new Map<string, number>();
    humanSearches.forEach((s) => {
      const key = `${s.from_iata ?? "?"} → ${s.to_iata ?? "?"}`;
      c.set(key, (c.get(key) ?? 0) + 1);
    });
    return [...c.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15);
  }, [humanSearches]);

  // Per-origin breakdown: dla każdego lotniska wylotu lista najczęściej
  // szukanych destynacji wraz z liczbą wyszukiwań. Sortowane malejąco po
  // łącznej liczbie wyszukiwań z danego origin.
  const byOrigin = useMemo(() => {
    const map = new Map<string, { total: number; dests: Map<string, number> }>();
    for (const s of humanSearches) {
      const from = (s.from_iata ?? "").toUpperCase();
      const to = (s.to_iata ?? "").toUpperCase();
      if (!from || !to) continue;
      const entry = map.get(from) ?? { total: 0, dests: new Map<string, number>() };
      entry.total += 1;
      entry.dests.set(to, (entry.dests.get(to) ?? 0) + 1);
      map.set(from, entry);
    }
    return [...map.entries()]
      .map(([from, { total, dests }]) => ({
        from,
        fromCity: airportByLowerCode(from)?.city ?? from,
        total,
        dests: [...dests.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([code, count]) => ({
            code,
            city: airportByLowerCode(code)?.city ?? code,
            country: airportByLowerCode(code)?.country ?? "",
            count,
          })),
      }))
      .sort((a, b) => b.total - a.total);
  }, [humanSearches]);

  // Daily series (last 30 days) for views and searches
  const dailySeries = useMemo(() => {
    const days = 30;
    // Lokalny klucz dnia (YYYY-MM-DD) — żeby bucket "dziś" obejmował zdarzenia
    // wykonane dziś w lokalnej strefie czasowej, a nie w UTC.
    const localKey = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const buckets: { date: string; label: string; views: number; searches: number; outbound: number }[] = [];
    const idx = new Map<string, number>();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today.getTime() - i * 86_400_000);
      const key = localKey(d);
      idx.set(key, buckets.length);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      buckets.push({ date: key, label: `${dd}.${mm}`, views: 0, searches: 0, outbound: 0 });
    }
    humanEvents.forEach((e) => {
      if (e.path.startsWith("/admin")) return;
      const k = localKey(new Date(e.created_at));
      const i = idx.get(k);
      if (i === undefined) return;
      if (!e.path.startsWith("/out/")) buckets[i].views++;
    });
    // Wyjścia liczymy z osobnego, pełnego strumienia 30 dni.
    humanOutboundEvents.forEach((e) => {
      const k = localKey(new Date(e.created_at));
      const i = idx.get(k);
      if (i !== undefined) buckets[i].outbound++;
    });
    humanSearches.forEach((s) => {
      const k = localKey(new Date(s.created_at));
      const i = idx.get(k);
      if (i !== undefined) buckets[i].searches++;
    });
    return buckets;
  }, [humanEvents, humanOutboundEvents, humanSearches, tick]);

  // Hourly series (last 24h) for views and searches
  const hourlySeries = useMemo(() => {
    const hours = 24;
    const now = new Date();
    const cur = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0);
    const buckets: { key: number; label: string; views: number; searches: number; outbound: number }[] = [];
    const idx = new Map<number, number>();
    for (let i = hours - 1; i >= 0; i--) {
      const d = new Date(cur.getTime() - i * 3_600_000);
      const k = d.getTime();
      idx.set(k, buckets.length);
      buckets.push({ key: k, label: `${String(d.getHours()).padStart(2, "0")}:00`, views: 0, searches: 0, outbound: 0 });
    }
    const bucketKey = (ts: string) => {
      const d = new Date(ts);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), 0, 0, 0).getTime();
    };
    humanEvents.forEach((e) => {
      if (e.path.startsWith("/admin")) return;
      const i = idx.get(bucketKey(e.created_at));
      if (i === undefined) return;
      if (!e.path.startsWith("/out/")) buckets[i].views++;
    });
    humanOutboundEvents.forEach((e) => {
      const i = idx.get(bucketKey(e.created_at));
      if (i !== undefined) buckets[i].outbound++;
    });
    humanSearches.forEach((s) => {
      const i = idx.get(bucketKey(s.created_at));
      if (i !== undefined) buckets[i].searches++;
    });
    return buckets;
  }, [humanEvents, humanOutboundEvents, humanSearches, tick]);

  if (!authChecked) return <div className="p-8 text-center text-muted-foreground">Ładowanie…</div>;
  if (!hasSession) return (
    <div className="min-h-screen">
      <Header />
      <section className="mx-auto w-full max-w-md px-4 py-20">
        <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-sunset shadow-glow">
          <Lock className="h-7 w-7 text-primary-foreground" />
        </div>
        <h1 className="font-display text-3xl font-bold">Panel admina</h1>
        <form onSubmit={submitLogin} className="mt-6 space-y-4 glass rounded-xl p-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Hasło</Label>
            <Input id="password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          <Button type="submit" variant="hero" className="w-full" disabled={loggingIn}>
            {loggingIn ? "Logowanie..." : "Zaloguj"}
          </Button>
        </form>
      </section>
      <Footer />
    </div>
  );
  if (!isAdmin) return (
    <div className="p-8 text-center">
      <p className="text-muted-foreground">Brak uprawnień.</p>
      <Button onClick={logout} variant="outline" className="mt-4">Wyloguj</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 glass">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div>
            <div className="font-display text-xl font-bold">Panel admina</div>
            <div className="text-xs text-muted-foreground">Tanie Latanie · live</div>
          </div>
          <div className="flex items-center gap-3">
            {searchHealth.alert && (
              <Badge
                variant={searchHealth.alert.level === "critical" ? "destructive" : "secondary"}
                className="gap-1.5"
                title={searchHealth.alert.reasons.join(" · ")}
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Search {searchHealth.alert.level === "critical" ? "KRYTYCZNY" : "alert"}: {searchHealth.alert.reasons.join(", ")}
              </Badge>
            )}
            <Badge variant="default" className="gap-1.5"><span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />{stats.online} online</Badge>
            <Button variant="outline" size="sm" onClick={logout}><LogOut className="mr-2 h-4 w-4" />Wyloguj</Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Stat icon={Users} label="Online (90s)" value={stats.online} />
          <Stat icon={Eye} label="Odsłony 24h" value={stats.last24} />
          <Stat icon={Activity} label="Unikalne 24h" value={stats.uniq24} />
          <Stat icon={Eye} label="Odsłony 7d" value={stats.last7} />
          <Stat icon={TrendingUp} label={'Kliknięć „Szukaj" 24h'} value={stats.out24} />
          <Stat
            icon={MousePointerClick}
            label={'Kliknięcia „Szukaj"'}
            value={submitClicks?.today ?? 0}
            sub={`wczoraj: ${submitClicks?.yesterday ?? 0} · dzień od 5:00`}
          />
        </div>

        <Tabs defaultValue="stats" className="mt-8">
          <TabsList>
            <TabsTrigger value="stats">Statystyki</TabsTrigger>
            <TabsTrigger value="searches">Wyszukiwania</TabsTrigger>
            <TabsTrigger value="origins">Po lotniskach</TabsTrigger>
            <TabsTrigger value="prices">Ceny</TabsTrigger>
            <TabsTrigger value="geomap">Mapa geo</TabsTrigger>
            <TabsTrigger value="messages">
              Wiadomości ({msgs.filter((m) => !m.read).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stats" className="mt-4 grid gap-4 md:grid-cols-2">
            <Card
              className={`p-4 md:col-span-2 ${
                searchHealth.alert?.level === "critical"
                  ? "border-destructive/60 bg-destructive/5"
                  : searchHealth.alert
                    ? "border-yellow-500/60 bg-yellow-500/5"
                    : ""
              }`}
            >
              <h3 className="mb-3 font-semibold flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${searchHealth.alert ? "text-destructive" : "text-muted-foreground"}`} />
                Zdrowie wyszukiwarki
                {searchHealth.alert && (
                  <Badge
                    variant={searchHealth.alert.level === "critical" ? "destructive" : "secondary"}
                    className="ml-1"
                  >
                    {searchHealth.alert.level === "critical" ? "krytyczny" : "alert"}
                  </Badge>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat
                  icon={Ban}
                  label="Anulowania (5 min)"
                  value={searchHealth.cancels5}
                  sub={`${searchHealth.cancels60} w 60 min`}
                />
                <Stat
                  icon={RotateCcw}
                  label="Restarty (5 min)"
                  value={searchHealth.restarts5}
                  sub={`${searchHealth.restarts60} w 60 min`}
                />
                <Stat
                  icon={Activity}
                  label="Trasy w pętli"
                  value={searchHealth.hotRoutes.length}
                  sub="≥5 restartów / ≥10 anuluj"
                />
                <Stat
                  icon={Search}
                  label="Powody (5 min)"
                  value={searchHealth.byReason5.reduce((s, [, n]) => s + n, 0)}
                  sub={searchHealth.byReason5.map(([r, n]) => `${r}:${n}`).join(" ") || "brak"}
                />
              </div>

              {searchHealth.alert && (
                <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                  <div className="font-medium text-destructive">
                    Search nie działa stabilnie — {searchHealth.alert.reasons.join(", ")}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Progi: 20 anulowań / 10 restartów w 5 min, lub trasa z ≥5 restartami (pętla F5).
                  </div>
                </div>
              )}

              {searchHealth.hotRoutes.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1 text-xs font-medium text-muted-foreground">Trasy z najgorszą sygnaturą (5 min)</div>
                  <div className="flex flex-wrap gap-2">
                    {searchHealth.hotRoutes.map(([route, v]) => (
                      <Badge key={route} variant="outline" className="font-mono text-xs">
                        {route} · cancel:{v.cancels} restart:{v.restarts}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {searchHealth.recent.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-muted-foreground">
                    Ostatnie zdarzenia (5 min) — {searchHealth.recent.length}
                  </summary>
                  <div className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs">
                    {searchHealth.recent.map((r, i) => {
                      const ageS = Math.round((Date.now() - +new Date(r.at)) / 1000);
                      return (
                        <div key={`${r.session}-${r.at}-${i}`} className="flex items-center gap-2 font-mono">
                          <span className="text-muted-foreground">{ageS}s</span>
                          <Badge
                            variant={r.kind === "cancel" ? "outline" : "secondary"}
                            className="text-[10px]"
                          >
                            {r.kind}
                          </Badge>
                          <span className="truncate">
                            {JSON.stringify(r.payload)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </Card>

            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><Activity className="h-4 w-4" />Live ścieżki</h3>
              {liveSessions.length === 0 && <p className="text-sm text-muted-foreground">Brak aktywnych sesji.</p>}
              <div className="space-y-2">
                {liveSessions.map(({ sid, evs }) => {
                  const last = evs[0];
                  const ageS = Math.round((Date.now() - +new Date(last.created_at)) / 1000);
                  const dbLastSearch = lastSearchBySession.get(sid);
                  const dbSearchAgeMs = dbLastSearch
                    ? Date.now() - +new Date(dbLastSearch.created_at)
                    : null;
                  // Fallback z URL: jeśli nie mamy świeżego wpisu w `search_queries`
                  // (np. dedup w sessionStorage, stary wpis sprzed >3 h, brak
                  // INSERTa przez network glitch), parsujemy `from`/`to`/`dd`
                  // z ostatniego eventu `/search` w bieżącej wizycie.
                  const urlSearch = (() => {
                    for (const e of evs) {
                      const qIdx = e.path.indexOf("?");
                      if (qIdx < 0 || !e.path.startsWith("/search")) continue;
                      const sp = new URLSearchParams(e.path.slice(qIdx + 1));
                      const from = sp.get("from")?.toUpperCase();
                      const to = sp.get("to")?.toUpperCase();
                      const dd = sp.get("dd");
                      if (!from || !to) continue;
                      return {
                        from_iata: from, to_iata: to,
                        depart_date: dd ? dd.split(",")[0] : null,
                        return_date: sp.get("rd")?.split(",")[0] ?? null,
                        pax: Number(sp.get("a") ?? 1) || 1,
                        trip_type: sp.get("ot") === "OneWay" ? "OneWay" : "RoundTrip",
                        service_class: sp.get("sc") ?? "economy",
                        direct_only: sp.get("d") === "1",
                        created_at: e.created_at,
                        _fromUrl: true as const,
                      };
                    }
                    return null;
                  })();
                  // Priorytet: świeży wpis z DB (≤3 h) > URL z bieżącej wizyty.
                  // Nie pokazujemy nic, jeśli oba są niedostępne lub starsze niż 3 h.
                  const STALE_MS = 3 * 60 * 60_000;
                  const dbFresh = dbLastSearch && dbSearchAgeMs !== null && dbSearchAgeMs < STALE_MS;
                  const lastSearch: (SearchQ | NonNullable<typeof urlSearch>) | null =
                    dbFresh ? dbLastSearch : urlSearch ?? null;
                  const searchAgeMs = lastSearch
                    ? Date.now() - +new Date(lastSearch.created_at)
                    : null;
                  const fmtAge = (ms: number) => {
                    const m = Math.round(ms / 60_000);
                    if (m < 1) return "przed chwilą";
                    if (m < 60) return `${m} min temu`;
                    const h = Math.round(m / 60);
                    if (h < 24) return `${h} h temu`;
                    const d = Math.round(h / 24);
                    return `${d} dni temu`;
                  };
                  const fromAirport = lastSearch?.from_iata
                    ? airportByLowerCode(lastSearch.from_iata.toLowerCase())
                    : null;
                  const toAirport = lastSearch?.to_iata
                    ? airportByLowerCode(lastSearch.to_iata.toLowerCase())
                    : null;
                  const fromIsCountry = !!fromAirport?.isCountry;
                  const toIsCountry = !!toAirport?.isCountry;
                  const fromCity = fromIsCountry
                    ? (fromAirport?.country ?? lastSearch?.from_iata ?? null)
                    : (fromAirport?.city ?? lastSearch?.from_iata ?? null);
                  const toCity = toIsCountry
                    ? (toAirport?.country ?? lastSearch?.to_iata ?? null)
                    : (toAirport?.city ?? lastSearch?.to_iata ?? null);
                  const fromIsMetro = !!fromAirport?.isMetro;
                  const toIsMetro = !!toAirport?.isMetro;
                  // Status: zielona — pewnie online (heartbeat <90 s),
                  // pomarańczowa — niepewne (90 s–3 min, użytkownik mógł
                  // przejść w tło / czytać stronę), czerwona — najpewniej
                  // już wyszedł (>3 min bez żadnego eventu).
                  const dot = ageS < 90 ? "bg-green-500" : ageS < 180 ? "bg-orange-500" : "bg-red-500";
                  const dotTitle = ageS < 90 ? "Online" : ageS < 180 ? "Niepewne — może być nieaktywny" : "Wyszedł";
                  // Najnowsze wzbogacone dane (IP/kraj/UA są ustawiane tylko
                  // przy odsłonach przez server function — outbound/keepalive
                  // ich nie ma). Bierzemy pierwszy event z dostępnymi danymi.
                  const enriched = evs.find((e) => e.country || e.ip || e.user_agent) ?? last;
                  const country = enriched.country ?? null;
                  const ip = enriched.ip ?? null;
                  const agent = classifyAgent(enriched.user_agent);
                  const device = deviceInfo(enriched.user_agent);
                  const deviceTitle = [
                    deviceKindLabel(device.kind),
                    device.model,
                    device.os,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  const deviceBadgeClass =
                    device.kind === "mobile"
                      ? "border-blue-500/40 text-blue-600 dark:text-blue-400"
                      : device.kind === "tablet"
                        ? "border-purple-500/40 text-purple-600 dark:text-purple-400"
                        : device.kind === "desktop"
                          ? "border-slate-500/40 text-slate-600 dark:text-slate-300"
                          : "border-border text-muted-foreground";
                  // Nowy vs powracający: bieżąca wizyta = burst eventów w
                  // ciągu ostatnich 5 min. Jeśli istnieje event tej samej
                  // sesji starszy niż 30 min przed początkiem bieżącej wizyty,
                  // user już u nas był → „Powracający". W przeciwnym razie
                  // „Nowy".
                  const visitStart = +new Date(evs[evs.length - 1].created_at);
                  const history = sessionTimes.get(sid) ?? [];
                  const GAP = 30 * 60_000;
                  const prevTimes = history.filter((t) => t < visitStart - GAP);
                  const isReturning = prevTimes.length > 0;
                  const lastSeenMs = isReturning ? Date.now() - prevTimes[prevTimes.length - 1] : null;
                  // Landing page = pierwszy event bieżącej wizyty (najstarszy
                  // w `evs`, bo posortowane malejąco). Jego `referrer` mówi,
                  // skąd user przyszedł, a jego `path` jest naszym proxy hasła.
                  const landingEv = evs[evs.length - 1];
                  const ref = referrerInfo(landingEv.referrer);
                  // Google praktycznie nigdy nie przekazuje frazy — pokazujemy ją
                  // tylko gdy faktycznie przyszła w utm_term. W przeciwnym razie
                  // dla wyszukiwarki pokazujemy landing path (to nie jest fraza,
                  // tylko strona wejścia — żeby nie wprowadzać w błąd).
                  const utmTerm = (landingEv.utm_term ?? "").trim() || null;
                  const landingPath = (landingEv.path || "/").split("?")[0];
                  const refBadgeClass =
                    ref.kind === "search"
                      ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                      : ref.kind === "social"
                        ? "border-pink-500/40 text-pink-600 dark:text-pink-400"
                        : ref.kind === "ai"
                          ? "border-violet-500/40 text-violet-600 dark:text-violet-400"
                          : ref.kind === "internal"
                            ? "border-border text-muted-foreground"
                            : ref.kind === "direct"
                              ? "border-amber-500/40 text-amber-600 dark:text-amber-400"
                              : "border-slate-500/40 text-slate-600 dark:text-slate-300";
                  return (
                    <div key={sid} className="rounded-lg border border-border/60 bg-card/40 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2 min-w-0">
                          <span title={dotTitle} className={`inline-block h-2.5 w-2.5 rounded-full ${dot} ${ageS < 90 ? "animate-pulse" : ""}`} />
                          <span title={country ?? "Nieznany kraj"} className="text-base leading-none">{flagEmoji(country)}</span>
                          {ip && <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{ip}</code>}
                          {country && <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{country}</span>}
                          {isReturning ? (
                            <Badge variant="outline" className="border-primary/40 text-primary" title={lastSeenMs !== null ? `Ostatnio: ${fmtAge(lastSeenMs)}` : undefined}>
                              ↻ Powracający{lastSeenMs !== null ? ` · ${fmtAge(lastSeenMs)}` : ""}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-green-500/40 text-green-600 dark:text-green-400">✨ Nowy</Badge>
                          )}
                          {agent.kind === "lovable" && <Badge variant="outline" className="border-primary/40 text-primary">{agent.label}</Badge>}
                          {agent.kind === "bot" && <Badge variant="outline" className="border-orange-500/40 text-orange-500">🤖 {agent.label}</Badge>}
                          {agent.kind === "headless" && <Badge variant="outline" className="border-orange-500/40 text-orange-500">{agent.label}</Badge>}
                          {device.kind !== "unknown" && (
                            <Badge variant="outline" className={deviceBadgeClass} title={deviceTitle || undefined}>
                              <span className="mr-1">{device.icon}</span>
                              {deviceKindLabel(device.kind)}
                              {device.model ? ` · ${device.model}` : ""}
                            </Badge>
                          )}
                          {(() => {
                            const t = themeBySession.get(sid);
                            if (!t) return null;
                            const isDark = t.theme === "dark";
                            return (
                              <Badge
                                variant="outline"
                                className={
                                  isDark
                                    ? "border-slate-500/40 text-slate-600 dark:text-slate-300"
                                    : "border-amber-500/40 text-amber-600 dark:text-amber-400"
                                }
                                title={`Motyw: ${isDark ? "ciemny" : "jasny"}`}
                              >
                                {isDark ? "🌙 Ciemny" : "☀️ Jasny"}
                              </Badge>
                            );
                          })()}
                          <Badge
                            variant="outline"
                            className={refBadgeClass}
                            title={
                              landingEv.referrer
                                ? `Referrer: ${landingEv.referrer}\nLanding: ${landingEv.path}`
                                : `Wejście bezpośrednie · Landing: ${landingEv.path}`
                            }
                          >
                            <span className="mr-1">{ref.icon}</span>
                            {ref.name}
                            {ref.kind === "search"
                              ? utmTerm
                                ? ` → "${utmTerm}"`
                                : ` · landing ${landingPath}`
                              : ""}
                          </Badge>
                          {(() => {
                            const geo = geoOriginBySession.get(sid);
                            if (!geo) return null;
                            const reasonLabel: Record<string, string> = {
                              city: "rozpoznane miasto",
                              nearest: "najbliższy hub po lat/lon",
                              country: "fallback per kraj",
                              default: "fallback domyślny (WAW)",
                            };
                             const display = hubDisplay(geo.hub);
                             const suffix = display !== geo.hub ? ` (${geo.hub}+)` : "";
                             const tooltip = [
                               `Podpowiedź startu: ${display}${display !== geo.hub ? ` — wszystkie lotniska (zawiera ${geo.hub})` : ""}`,
                               geo.city ? `Miasto IP: ${geo.city}` : null,
                               geo.country ? `Kraj IP: ${geo.country}` : null,
                               `Powód: ${reasonLabel[geo.reason] ?? geo.reason}`,
                             ].filter(Boolean).join("\n");
                             return (
                               <Badge
                                 variant="outline"
                                 className="border-cyan-500/40 text-cyan-600 dark:text-cyan-400"
                                 title={tooltip}
                               >
                                 <Plane className="mr-1 h-3 w-3" />
                                 Start: {display}{suffix}
                                 {geo.city ? ` · ${geo.city}` : ""}
                               </Badge>
                             );
                          })()}
                          <span className="font-mono text-xs text-muted-foreground">{sid.slice(0, 8)}…</span>
                        </div>
                        <Badge variant={ageS < 60 ? "default" : "secondary"}>{ageS < 60 ? `${ageS}s temu` : `${Math.round(ageS / 60)} min temu`}</Badge>
                      </div>
                      {/* Pokazujemy wyszukiwanie, jeśli pochodzi z bieżącej wizyty
                          (≤ 3 h) — albo z DB (`search_queries`), albo sparsowane
                          z URL ostatniego eventu `/search`. Stare wpisy z innych
                          sesji tej samej osoby są odcinane progiem 3 h. */}
                      {lastSearch && searchAgeMs !== null && searchAgeMs < 3 * 60 * 60_000 && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                          <Plane className="h-3.5 w-3.5 text-primary" />
                          <span className="font-medium">
                            {fromCity} ({lastSearch.from_iata}{fromIsCountry ? " · CAŁY KRAJ" : fromIsMetro ? " · GRUPA" : ""}) → {toCity} ({lastSearch.to_iata}{toIsCountry ? " · CAŁY KRAJ" : toIsMetro ? " · GRUPA" : ""})
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {lastSearch.depart_date ?? "?"}
                            {lastSearch.return_date ? ` – ${lastSearch.return_date}` : ""}
                            {lastSearch.pax ? ` · ${lastSearch.pax} pax` : ""}
                            {` · szukał ${fmtAge(searchAgeMs)}`}
                          </span>
                          {(() => {
                            const f = (lastSearch.from_iata ?? "").toUpperCase();
                            const t = (lastSearch.to_iata ?? "").toUpperCase();
                            const dd = lastSearch.depart_date ?? "";
                            if (!/^[A-Z]{3}$/.test(f) || !/^[A-Z]{3}$/.test(t) || !/^\d{4}-\d{2}-\d{2}$/.test(dd)) return null;
                            const isOneWay = lastSearch.trip_type === "OneWay";
                            const rd = !isOneWay && lastSearch.return_date && /^\d{4}-\d{2}-\d{2}$/.test(lastSearch.return_date) ? lastSearch.return_date : null;
                            const a = lastSearch.pax && lastSearch.pax >= 1 && lastSearch.pax <= 9 ? lastSearch.pax : 1;
                            const sc = lastSearch.service_class ?? "economy";
                            const params = new URLSearchParams();
                            if (isOneWay) params.set("ot", "OneWay");
                            params.set("from", f);
                            params.set("to", t);
                            params.set("dd", dd);
                            if (rd) params.set("rd", rd);
                            params.set("a", String(a));
                            if (sc !== "economy") params.set("sc", sc);
                            if (lastSearch.direct_only) params.set("d", "1");
                            const href = `/search?${params.toString()}`;
                            return (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Otwórz identyczne wyszukiwanie w nowej karcie"
                                className="inline-flex items-center gap-1 rounded border border-primary/40 px-1.5 py-0.5 text-xs text-primary hover:bg-primary/10"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Otwórz
                              </a>
                            );
                          })()}
                          {(() => {
                            const filters: { label: string; cls: string }[] = [];
                            if (lastSearch.trip_type && lastSearch.trip_type !== "RoundTrip") {
                              filters.push({ label: "W jedną stronę", cls: "border-amber-500/40 text-amber-600 dark:text-amber-400" });
                            }
                            if (lastSearch.service_class && lastSearch.service_class !== "economy") {
                              const map: Record<string, string> = {
                                premium_economy: "Premium economy",
                                business: "Business",
                                first: "First",
                              };
                              filters.push({
                                label: map[lastSearch.service_class] ?? lastSearch.service_class,
                                cls: "border-violet-500/40 text-violet-600 dark:text-violet-400",
                              });
                            }
                            if (lastSearch.direct_only) {
                              filters.push({ label: "Tylko bezpośrednie", cls: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" });
                            }
                            if (filters.length === 0) return null;
                            return (
                              <span className="flex flex-wrap items-center gap-1" title="Filtry zmienione z domyślnych">
                                {filters.map((f) => (
                                  <Badge key={f.label} variant="outline" className={f.cls}>
                                    {f.label}
                                  </Badge>
                                ))}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                      {(() => {
                        const st = searchStateBySession.get(sid);
                        if (!st) return null;
                        const ageMs = Date.now() - st.at;
                        const FRESH_MS = 30_000;
                        const LIVE_MS = 3 * 60_000;
                        const fresh = ageMs < FRESH_MS;
                        const stale = ageMs >= LIVE_MS;
                        // Po przekroczeniu progu „live" (3 min) loading/empty tracą
                        // sens (nie wiemy, czy user nadal czeka) — pokazujemy tylko
                        // ostatni znany wynik / błąd jako wartość historyczną.
                        if (stale && st.status !== "results" && st.status !== "error") return null;
                        // Wykryj rozjazd: badge state'u dotyczy INNEJ trasy niż
                        // ostatnie szukanie z `search_queries`. To się dzieje
                        // gdy user wchodzi na inną trasę przez URL/link bez
                        // submitu formularza (search_queries się nie aktualizuje),
                        // ale `/search` faktycznie renderuje nową trasę.
                        // W takim wypadku ŚWIEŻY state = źródło prawdy — pokaż
                        // go jako „aktualnie ogląda", żeby admin wiedział co
                        // user widzi TERAZ, a nie co kiedyś wpisał w formularz.
                        const currentRoute = lastSearch?.from_iata && lastSearch?.to_iata
                          ? `${lastSearch.from_iata}→${lastSearch.to_iata}`
                          : null;
                        const stateNewerThanForm = lastSearch
                          ? st.at > +new Date(lastSearch.created_at)
                          : true;
                        const routeMismatch = !!(currentRoute && st.route && st.route !== currentRoute);
                        const overrideRoute = routeMismatch && stateNewerThanForm;
                        const cls = stale
                          ? "border-muted-foreground/30 text-muted-foreground bg-muted/30"
                          : st.status === "error"
                            ? "border-red-500/50 text-red-600 dark:text-red-400 bg-red-500/5"
                            : st.status === "results"
                              ? "border-green-500/50 text-green-600 dark:text-green-400 bg-green-500/5"
                              : st.status === "loading"
                                ? "border-blue-500/50 text-blue-600 dark:text-blue-400 bg-blue-500/5"
                                : "border-amber-500/50 text-amber-600 dark:text-amber-400 bg-amber-500/5";
                        const icon =
                          st.status === "error" ? "⚠️" :
                          st.status === "results" ? "✅" :
                          st.status === "loading" ? "⏳" : "∅";
                        const baseLabel =
                          st.status === "error"
                            ? `Błąd wyszukiwania${st.errorMsg ? `: ${st.errorMsg}` : ""}`
                            : st.status === "results"
                              ? `Widzi ${st.visible} ${st.visible === 1 ? "wynik" : st.visible >= 2 && st.visible <= 4 ? "wyniki" : "wyników"}`
                              : st.status === "loading"
                                ? `Szuka… (jeszcze 0 wyników${st.pairs ? ` · ${st.pairs} par tras` : ""})`
                                : "Brak wyników (zapytanie zakończone, pusto)";
                        const ageMin = Math.max(1, Math.round(ageMs / 60_000));
                        const prefix = overrideRoute ? `[teraz na ${st.route}] ` : "";
                        const label = stale ? `${prefix}${baseLabel} (sprzed ${ageMin} min)` : `${prefix}${baseLabel}`;
                        return (
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                            <Badge
                              variant="outline"
                              className={`${cls} ${fresh && st.status === "loading" ? "animate-pulse" : ""}`}
                              title={stale ? `Ostatni znany stan sprzed ${ageMin} min — user mógł już zamknąć kartę lub przeładować.` : (st.errorMsg ?? undefined)}
                            >
                              <span className="mr-1">{icon}</span>
                              {label}
                            </Badge>
                            {st.partial && !stale && (
                              <Badge
                                variant="outline"
                                className="border-amber-500/50 text-amber-600 dark:text-amber-400 bg-amber-500/5"
                                title={st.partial}
                              >
                                <span className="mr-1">⚠️</span>
                                {st.partial}
                              </Badge>
                            )}
                            {st.truncated && !stale && (
                              <Badge
                                variant="outline"
                                className="border-sky-500/50 text-sky-600 dark:text-sky-400 bg-sky-500/5"
                                title={`User widzi badge ograniczenia: ${st.truncated}. Fan-out został przycięty — sprawdź, czy to nie ucina najlepszych ofert.`}
                              >
                                <span className="mr-1">✂️</span>
                                {st.truncated}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {fresh ? "live" : ageMs < LIVE_MS ? `${Math.round(ageMs / 1000)}s temu` : `${ageMin} min temu`}
                              {st.route ? ` · ${st.route}` : ""}
                            </span>
                          </div>
                        );
                      })()}
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm">
                        {evs.slice(0, 8).reverse().map((e, i, arr) => {

                          const qIdx = e.path.indexOf("?");
                          const pathname = qIdx >= 0 ? e.path.slice(0, qIdx) : e.path;
                          const qs = qIdx >= 0 ? e.path.slice(qIdx + 1) : "";
                          const sp = new URLSearchParams(qs);
                          // parametry „bazowe" trasy (route identity) — nie pokazujemy jako chipy
                          const baseKeys = new Set(["ot", "from", "to", "dd", "rd", "a", "c", "i", "sc"]);
                          const labelMap: Record<string, (v: string) => string> = {
                            sort: (v) => {
                              const m: Record<string, string> = {
                                "recommended": "Polecane",
                                "price-asc": "Najtaniej",
                                "price-desc": "Najdrożej",
                                "soonest": "Najszybciej",
                                "duration": "Najkrócej",
                              };
                              return `sortuj: ${m[v] ?? v}`;
                            },
                            stops: (v) => v === "direct" ? "bezpośrednie" : v === "1" ? "≤1 przesiadka" : `stops: ${v}`,
                            d: (v) => v === "1" ? "bezpośrednie" : `d: ${v}`,
                            bag: (v) => v === "checked" ? "z bagażem" : v === "carry" ? "z bagażem podręcznym" : `bagaż: ${v}`,
                            airline: (v) => `linia: ${v}`,
                            via: (v) => `przez: ${v}`,
                            maxPrice: (v) => `≤ ${v} zł`,
                            minPrice: (v) => `≥ ${v} zł`,
                            dep: (v) => `wylot: ${v}`,
                            arr: (v) => `przylot: ${v}`,
                          };
                          const ignoreKeys = new Set([
                            "forceHideBadge", "gclid", "fbclid", "msclkid", "yclid",
                            "ref", "_branch_match_id", "mc_cid", "mc_eid",
                          ]);
                          const chips: { key: string; label: string }[] = [];
                          const seenLabels = new Set<string>();
                          sp.forEach((v, k) => {
                            if (baseKeys.has(k)) return;
                            if (ignoreKeys.has(k)) return;
                            if (k.startsWith("utm_")) return;
                            const fmt = labelMap[k];
                            const label = fmt ? fmt(v) : `${k}: ${v}`;
                            // dedupe po etykiecie (np. d=1 i stops=direct dają to samo "bezpośrednie")
                            if (seenLabels.has(label)) return;
                            seenLabels.add(label);
                            chips.push({ key: `${k}=${v}`, label });
                          });
                          const isSearch = pathname === "/search" && qs.length > 0;
                          const fullHref = e.path;
                          return (
                            <span key={e.id} className="flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs">
                                {isSearch ? (
                                  <a
                                    href={fullHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline decoration-dotted underline-offset-2 hover:text-primary"
                                    title={`Otwórz to wyszukiwanie w nowej karcie: ${fullHref}`}
                                  >
                                    <code>{pathname}</code>
                                  </a>
                                ) : (
                                  <code>{pathname}</code>
                                )}
                                {chips.map((c) => (
                                  <Badge key={c.key} variant="outline" className="h-4 px-1 text-[10px] font-normal border-primary/40 text-primary">
                                    {c.label}
                                  </Badge>
                                ))}
                              </span>
                              {i < arr.length - 1 && <span className="text-muted-foreground">→</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4" />Odsłony stron i wyszukiwania lotów — 24 h</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={hourlySeries} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="label" stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} interval={2} />
                    <YAxis stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} allowDecimals={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground)" }}
                      labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
                      itemStyle={{ color: "var(--popover-foreground)" }}
                      cursor={{ stroke: "var(--primary)", strokeOpacity: 0.3 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: "var(--foreground)" }} />
                    <Line type="monotone" dataKey="views" stroke="var(--primary)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} name="Odsłony" />
                    <Line type="monotone" dataKey="searches" stroke="var(--accent)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} name="Wyszukiwania" />
                    <Line type="monotone" dataKey="outbound" stroke="#22c55e" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} name={'Kliknięcia „Szukaj"'} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Ostatnie 24 godziny · co godzinę.</p>
            </Card>
            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4" />Kliknięcia „Szukaj lotów" — 24 h</h3>
              <div className="h-60 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={hourlySeries} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="label" stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} interval={2} />
                    <YAxis stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} allowDecimals={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground)" }}
                      labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
                      itemStyle={{ color: "var(--popover-foreground)" }}
                      cursor={{ stroke: "var(--primary)", strokeOpacity: 0.3 }}
                    />
                    <Line type="monotone" dataKey="searches" stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Wyszukiwania" />
                    <Line type="monotone" dataKey="outbound" stroke="var(--primary)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} name={'Kliknięcia „Szukaj"'} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Kliknięcia przycisku „Szukaj lotów" w wyszukiwarce Aviasales · porównanie z odsłonami.</p>
            </Card>
            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4" />Odsłony stron — 30 dni</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailySeries} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="label" stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} />
                    <YAxis stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} allowDecimals={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground)" }}
                      labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
                      itemStyle={{ color: "var(--popover-foreground)" }}
                      cursor={{ stroke: "var(--primary)", strokeOpacity: 0.3 }}
                    />
                    <Line type="monotone" dataKey="views" stroke="var(--primary)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} name="Odsłony" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Auto-odświeżanie co 60 s.</p>
            </Card>
            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><Search className="h-4 w-4" />Wyszukiwania lotów — 30 dni</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailySeries} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="label" stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} />
                    <YAxis stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} allowDecimals={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground)" }}
                      labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
                      itemStyle={{ color: "var(--popover-foreground)" }}
                      cursor={{ stroke: "var(--accent)", strokeOpacity: 0.3 }}
                    />
                    <Line type="monotone" dataKey="searches" stroke="var(--accent)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} name="Wyszukiwania" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Auto-odświeżanie co 60 s.</p>
            </Card>
            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4" />Kliknięcia „Szukaj lotów" — 30 dni</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailySeries} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="label" stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} />
                    <YAxis stroke="var(--foreground)" tick={{ fill: "var(--foreground)", fontSize: 12 }} tickLine={{ stroke: "var(--border)" }} axisLine={{ stroke: "var(--border)" }} allowDecimals={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground)" }}
                      labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
                      itemStyle={{ color: "var(--popover-foreground)" }}
                      cursor={{ stroke: "var(--primary)", strokeOpacity: 0.3 }}
                    />
                    <Line type="monotone" dataKey="outbound" stroke="var(--primary)" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} name={'Kliknięcia „Szukaj"'} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Kliknięcia przycisku „Szukaj lotów" w wyszukiwarce Aviasales.</p>
            </Card>
            <Card className="p-4">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><Eye className="h-4 w-4" />Top strony (7d)</h3>
              <ul className="space-y-1.5 text-sm">
                {stats.topPaths.map(([p, n]) => (
                  <li key={p} className="flex justify-between"><code className="text-muted-foreground">{p}</code><span className="font-mono">{n}</span></li>
                ))}
              </ul>
            </Card>
            <Card className="p-4">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><Activity className="h-4 w-4" />Źródła ruchu (7d)</h3>
              <ul className="space-y-1.5 text-sm">
                {stats.topRefs.map(([p, n]) => (
                  <li key={p} className="flex justify-between"><span className="text-muted-foreground">{p}</span><span className="font-mono">{n}</span></li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">
                Uwaga: Google rzadko przekazuje frazy wyszukiwania. Aby je widzieć, dodaj integrację z Google Search Console lub używaj parametrów UTM.
              </p>
            </Card>
            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><Globe className="h-4 w-4" />Gdziekolwiek — intencje i kliknięcia (24h)</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Intencje</div>
                  <div className="text-2xl font-mono font-bold">{anywhereStats.intents}</div>
                  <ul className="mt-2 space-y-1 text-xs">
                    {anywhereStats.bySource.map(([s, n]) => (
                      <li key={s} className="flex justify-between"><span className="text-muted-foreground">{s}</span><span className="font-mono">{n}</span></li>
                    ))}
                    {anywhereStats.bySource.length === 0 && <li className="text-muted-foreground">brak danych</li>}
                  </ul>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Kliknięcia w kierunki · CTR</div>
                  <div className="text-2xl font-mono font-bold">{anywhereStats.clicks} <span className="text-sm text-muted-foreground">· {anywhereStats.ctr}%</span></div>
                  <div className="mt-2 text-xs uppercase text-muted-foreground">Top kierunki</div>
                  <ul className="mt-1 space-y-1 text-xs">
                    {anywhereStats.topDestinations.map(([code, info]) => (
                      <li key={code} className="flex justify-between gap-2">
                        <span className="truncate"><code>{code}</code>{info.country ? ` · ${info.country}` : ""}</span>
                        <span className="font-mono shrink-0">{info.n}{info.minPrice ? ` · od ${info.minPrice} zł` : ""}</span>
                      </li>
                    ))}
                    {anywhereStats.topDestinations.length === 0 && <li className="text-muted-foreground">brak kliknięć</li>}
                  </ul>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Top frazy (keyword)</div>
                  <ul className="mt-1 space-y-1 text-xs">
                    {anywhereStats.topQueries.map(([q, n]) => (
                      <li key={q} className="flex justify-between gap-2"><span className="truncate">„{q}"</span><span className="font-mono shrink-0">{n}</span></li>
                    ))}
                    {anywhereStats.topQueries.length === 0 && <li className="text-muted-foreground">brak fraz</li>}
                  </ul>
                  <div className="mt-3 text-xs uppercase text-muted-foreground">Ostatnie intencje</div>
                  <ul className="mt-1 space-y-1 text-xs">
                    {anywhereStats.recent.slice(0, 5).map((r, i) => (
                      <li key={i} className="truncate text-muted-foreground">
                        <span className="font-mono">{new Date(r.at).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}</span>
                        {" · "}{r.data.source ?? "?"}{r.data.origin ? ` (${r.data.origin})` : ""}{r.data.query ? ` · „${r.data.query}"` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><Users className="h-4 w-4" />Języki i kraje odwiedzających (30 dni)</h3>
              {(() => {
                const since = Date.now() - 30 * 86_400_000;
                // Per sesja bierzemy 1 język i 1 kraj — żeby nie liczyć
                // pojedynczej sesji wielokrotnie.
                const langBySession = new Map<string, string>();
                const countryBySession = new Map<string, string>();
                for (const e of humanEvents) {
                  if (e.path.startsWith("/admin")) continue;
                  if (new Date(e.created_at).getTime() < since) continue;
                  if (!langBySession.has(e.session_id)) {
                    const l = primaryLanguage(e.accept_language);
                    if (l) langBySession.set(e.session_id, l);
                  }
                  if (!countryBySession.has(e.session_id)) {
                    if (e.country) countryBySession.set(e.session_id, e.country.toUpperCase());
                  }
                }
                const tally = (m: Map<string, string>) => {
                  const c = new Map<string, number>();
                  for (const v of m.values()) c.set(v, (c.get(v) ?? 0) + 1);
                  return [...c.entries()].sort((a, b) => b[1] - a[1]);
                };
                const langs = tally(langBySession);
                const countries = tally(countryBySession);
                const langTotal = langs.reduce((s, [, n]) => s + n, 0);
                const countryTotal = countries.reduce((s, [, n]) => s + n, 0);
                if (langs.length === 0 && countries.length === 0) {
                  return <p className="text-sm text-muted-foreground">Brak danych — wzbogacanie odsłon o IP/kraj/język działa od teraz, dane będą się akumulować.</p>;
                }
                return (
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="mb-2 text-sm font-semibold text-muted-foreground">Języki przeglądarki</h4>
                      <ul className="space-y-1.5 text-sm">
                        {langs.slice(0, 12).map(([code, n]) => {
                          const pct = langTotal > 0 ? Math.round((n / langTotal) * 100) : 0;
                          return (
                            <li key={code} className="flex items-center justify-between gap-3">
                              <span className="flex items-center gap-2"><code className="rounded bg-muted px-1.5 py-0.5 text-xs uppercase">{code}</code>{languageName(code)}</span>
                              <span className="font-mono text-xs text-muted-foreground">{n} · {pct}%</span>
                            </li>
                          );
                        })}
                        {langs.length === 0 && <li className="text-muted-foreground">brak</li>}
                      </ul>
                    </div>
                    <div>
                      <h4 className="mb-2 text-sm font-semibold text-muted-foreground">Kraje (z IP)</h4>
                      <ul className="space-y-1.5 text-sm">
                        {countries.slice(0, 12).map(([cc, n]) => {
                          const pct = countryTotal > 0 ? Math.round((n / countryTotal) * 100) : 0;
                          return (
                            <li key={cc} className="flex items-center justify-between gap-3">
                              <span className="flex items-center gap-2"><span className="text-base leading-none">{flagEmoji(cc)}</span>{cc}</span>
                              <span className="font-mono text-xs text-muted-foreground">{n} · {pct}%</span>
                            </li>
                          );
                        })}
                        {countries.length === 0 && <li className="text-muted-foreground">brak</li>}
                      </ul>
                    </div>
                  </div>
                );
              })()}
              <p className="mt-3 text-xs text-muted-foreground">Liczone per unikalna sesja w ostatnich 30 dniach.</p>
            </Card>
            <Card className="p-4 md:col-span-2">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4" />Ostatnie kliknięcia „Szukaj lotów" (50)</h3>
              {(() => {
                const outs = humanOutboundEvents.slice(0, 50);
                if (outs.length === 0) return <p className="text-sm text-muted-foreground">Brak kliknięć.</p>;
                const parseRef = (ref: string | null) => {
                  if (!ref) return null;
                  try {
                    const u = new URL(ref);
                    const fs = (u.searchParams.get("flightSearch") ?? "").toUpperCase();
                    const m = fs.match(/^([A-Z]{3})(\d{2})(\d{2})([A-Z]{3})(?:(\d{2})(\d{2}))?/);
                    const from = m?.[1] ?? u.searchParams.get("from")?.toUpperCase();
                    const to = m?.[4] ?? u.searchParams.get("to")?.toUpperCase();
                    const dd = m ? `${m[2]}.${m[3]}` : u.searchParams.get("dd");
                    const rd = m?.[5] ? `${m[5]}.${m[6]}` : u.searchParams.get("rd");
                    return { from, to, dd, rd, path: u.pathname };
                  } catch {
                    return null;
                  }
                };
                return (
                  <ul className="space-y-1.5 text-sm max-h-[28rem] overflow-auto">
                    {outs.map((e) => {
                      const p = parseRef(e.referrer);
                      const route = p?.from && p?.to ? `${p.from}→${p.to}` : (p?.path ?? "—");
                      const dates = p?.dd ? `${p.dd}${p.rd ? ` / ${p.rd}` : ""}` : "";
                      return (
                        <li key={e.id} className="flex justify-between gap-2 border-b border-border/40 pb-1">
                          <span className="font-mono">{route}</span>
                          <span className="text-muted-foreground text-xs">
                            {e.utm_term && <span className="mr-2 font-mono text-emerald-500">{e.utm_term}</span>}
                            {dates && <span className="mr-2">{dates}</span>}
                            {new Date(e.created_at).toLocaleString("pl")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
              <p className="mt-2 text-xs text-muted-foreground">Trasa i data wylotu odczytane z wyszukiwania (np. KTW→STN, 15.12).</p>
            </Card>
          </TabsContent>

          <TabsContent value="searches" className="mt-4 grid gap-4 md:grid-cols-2">
            <Card className="p-4">
              <h3 className="mb-3 font-semibold flex items-center gap-2"><Search className="h-4 w-4" />Top trasy</h3>
              {topSearches.length === 0 && <p className="text-sm text-muted-foreground">Brak danych.</p>}
              <ul className="space-y-1.5 text-sm">
                {topSearches.map(([k, n]) => (
                  <li key={k} className="flex justify-between"><code className="font-mono">{k}</code><span className="font-mono">{n}</span></li>
                ))}
              </ul>
            </Card>
            <Card className="p-4">
              <h3 className="mb-3 font-semibold">Ostatnie wyszukiwania</h3>
              <ul className="space-y-1.5 text-sm max-h-96 overflow-auto">
                {searches.slice(0, 50).map((s) => (
                  <li key={s.id} className="flex justify-between gap-2 border-b border-border/40 pb-1">
                    <span className="font-mono">{s.from_iata}→{s.to_iata}</span>
                    <span className="text-muted-foreground text-xs">{s.depart_date}{s.return_date ? ` / ${s.return_date}` : ""} · {new Date(s.created_at).toLocaleString("pl")}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </TabsContent>

          <TabsContent value="origins" className="mt-4 space-y-3">
            {byOrigin.length === 0 && <p className="text-muted-foreground">Brak danych o wyszukiwaniach.</p>}
            {byOrigin.map(({ from, fromCity, total, dests }) => (
              <Card key={from} className="p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Plane className="h-4 w-4" />
                    {fromCity} <code className="text-xs text-muted-foreground">({from})</code>
                  </h3>
                  <Badge variant="secondary">{total.toLocaleString("pl")} wyszukiwań</Badge>
                </div>
                <ul className="grid gap-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  {dests.slice(0, 30).map((d) => {
                    const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
                    return (
                      <li key={d.code} className="flex items-center justify-between gap-2 border-b border-border/40 py-1">
                        <span className="min-w-0 truncate">
                          <span className="font-medium">{d.city}</span>
                          {d.country && <span className="text-muted-foreground"> · {d.country}</span>}
                          <code className="ml-1.5 text-xs text-muted-foreground">({d.code})</code>
                        </span>
                        <span className="font-mono text-xs whitespace-nowrap">
                          {d.count} <span className="text-muted-foreground">({pct}%)</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="prices" className="mt-4 space-y-4">
            <PricesTab
              stats={priceStats}
              loading={pricesLoading}
              refreshing={refreshingPrices}
              onReload={loadPriceStats}
              onTriggerRefresh={triggerRefresh}
            />
          </TabsContent>

          <TabsContent value="geomap" className="mt-4">
            <AdminGeoMap />
          </TabsContent>

          <TabsContent value="messages" className="mt-4 space-y-3">
            {msgs.length === 0 && <p className="text-muted-foreground">Brak wiadomości.</p>}
            {msgs.map((m) => (
              <Card key={m.id} className={`p-4 ${!m.read ? "border-primary/50" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{m.name}</span>
                      <a href={`mailto:${m.email}`} className="text-sm text-primary hover:underline">{m.email}</a>
                      {!m.read && <Badge>Nowa</Badge>}
                      <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString("pl")}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm">{m.message}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {!m.read && <Button size="sm" variant="outline" onClick={() => markRead(m.id)}><Mail className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="destructive" onClick={() => deleteMsg(m.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number; sub?: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="mt-1 font-display text-3xl font-bold">{value.toLocaleString("pl")}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function fmtAgo(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return "przed chwilą";
  if (m < 60) return `${m} min temu`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h temu`;
  const d = Math.round(h / 24);
  return `${d} dni temu`;
}

function PricesTab({
  stats,
  loading,
  refreshing,
  onReload,
  onTriggerRefresh,
}: {
  stats: PriceStats | null;
  loading: boolean;
  refreshing: boolean;
  onReload: () => void;
  onTriggerRefresh: (offset: number) => void;
}) {
  if (loading && !stats) return <p className="text-muted-foreground">Ładowanie statystyk cen…</p>;
  if (!stats) return (
    <Card className="p-4">
      <p className="text-muted-foreground">Brak danych. <Button size="sm" variant="outline" className="ml-2" onClick={onReload}>Spróbuj ponownie</Button></p>
    </Card>
  );

  const s = stats.summary;
  const fresh = s.total - s.stale_24h;
  const pctFresh = s.total > 0 ? Math.round((fresh / s.total) * 100) : 0;

  // pogrupuj uruchomienia cron-a per dobę (data startu)
  const runsByDay = new Map<string, CronRun[]>();
  for (const r of stats.runs) {
    const d = new Date(r.start_time);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const arr = runsByDay.get(key) ?? [];
    arr.push(r);
    runsByDay.set(key, arr);
  }
  const dayKeys = [...runsByDay.keys()].sort().reverse().slice(0, 7);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat icon={Plane} label="Par w cache" value={s.total} />
        <Stat icon={CheckCircle2} label={`Świeże (≤24h, ${pctFresh}%)`} value={fresh} sub={`${s.stale_24h} starszych`} />
        <Stat icon={Clock} label="Najnowszy refresh" value={0} sub={fmtAgo(s.newest_checked_at)} />
        <Stat icon={Clock} label="Najstarszy refresh" value={0} sub={fmtAgo(s.oldest_checked_at)} />
      </div>

      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="font-semibold flex items-center gap-2"><RefreshCw className="h-4 w-4" />Statystyki cen</h3>
          <Button size="sm" variant="outline" onClick={onReload} disabled={loading}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Odśwież widok
          </Button>
        </div>
        <div className="grid gap-2 text-sm sm:grid-cols-2 md:grid-cols-4">
          <div><span className="text-muted-foreground">Średnia realna:</span> <span className="font-mono">{s.avg_real ?? "—"} zł</span></div>
          <div><span className="text-muted-foreground">Średnia „od":</span> <span className="font-mono">{s.avg_display ?? "—"} zł</span></div>
          <div><span className="text-muted-foreground">Min „od":</span> <span className="font-mono">{s.min_display ?? "—"} zł</span></div>
          <div><span className="text-muted-foreground">Max „od":</span> <span className="font-mono">{s.max_display ?? "—"} zł</span></div>
          <div><span className="text-muted-foreground">Starsze niż 24h:</span> <span className="font-mono">{s.stale_24h}</span></div>
          <div><span className="text-muted-foreground">Starsze niż 48h:</span> <span className="font-mono">{s.stale_48h}</span></div>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4">
          <h3 className="mb-3 font-semibold">Najstarsze pary (najpilniejsze do refresh)</h3>
          {stats.oldest.length === 0 && <p className="text-sm text-muted-foreground">brak</p>}
          <ul className="space-y-1.5 text-sm">
            {stats.oldest.map((r) => (
              <li key={`${r.from_iata}-${r.to_iata}`} className="flex items-center justify-between gap-2 border-b border-border/40 pb-1">
                <span className="font-mono">{r.from_iata}→{r.to_iata}</span>
                <span className="text-xs text-muted-foreground">real {r.real_min_price} → od {r.display_price} zł · {fmtAgo(r.checked_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-4">
          <h3 className="mb-3 font-semibold">Najświeższe pary</h3>
          {stats.newest.length === 0 && <p className="text-sm text-muted-foreground">brak</p>}
          <ul className="space-y-1.5 text-sm">
            {stats.newest.map((r) => (
              <li key={`${r.from_iata}-${r.to_iata}`} className="flex items-center justify-between gap-2 border-b border-border/40 pb-1">
                <span className="font-mono">{r.from_iata}→{r.to_iata}</span>
                <span className="text-xs text-muted-foreground">real {r.real_min_price} → od {r.display_price} zł · {fmtAgo(r.checked_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="font-semibold flex items-center gap-2"><Clock className="h-4 w-4" />Zaplanowane zadania cron ({stats.jobs.length})</h3>
        </div>
        {stats.jobs.length === 0 && <p className="text-sm text-muted-foreground">Brak zadań cron. Zadania są dodawane automatycznie podczas konfiguracji.</p>}
        <ul className="grid gap-1.5 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {stats.jobs.map((j) => {
            const offMatch = j.jobname.match(/-(\d+)$/);
            const offset = offMatch ? parseInt(offMatch[1], 10) : 0;
            return (
              <li key={j.jobname} className="flex items-center justify-between gap-2 rounded border border-border/40 bg-card/40 px-2 py-1.5">
                <span className="min-w-0 truncate">
                  <code className="text-xs">{j.jobname}</code>
                  <span className="ml-2 text-xs text-muted-foreground">{j.schedule} UTC</span>
                  {!j.active && <Badge variant="outline" className="ml-2 border-orange-500/40 text-orange-600">wyłączone</Badge>}
                </span>
                <Button size="sm" variant="ghost" disabled={refreshing} onClick={() => onTriggerRefresh(offset)} title={`Uruchom teraz offset ${offset}`}>
                  <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                </Button>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card className="p-4">
        <h3 className="mb-3 font-semibold flex items-center gap-2"><Activity className="h-4 w-4" />Historia uruchomień cron-a (ostatnie 60)</h3>
        {stats.runs.length === 0 && <p className="text-sm text-muted-foreground">Brak zarejestrowanych uruchomień. Pierwsze odświeżenie nastąpi o ~03:00 UTC.</p>}
        <div className="space-y-3">
          {dayKeys.map((day) => {
            const runs = runsByDay.get(day) ?? [];
            const ok = runs.filter((r) => r.status === "succeeded").length;
            const fail = runs.filter((r) => r.status !== "succeeded" && r.status !== "running").length;
            return (
              <div key={day}>
                <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium">{day}</span>
                  <span>·</span>
                  <span className="text-green-600 dark:text-green-400">{ok} ok</span>
                  {fail > 0 && <><span>·</span><span className="text-red-600 dark:text-red-400">{fail} błędów</span></>}
                </div>
                <ul className="space-y-1 text-xs">
                  {runs.map((r, i) => {
                    const isOk = r.status === "succeeded";
                    const isRunning = r.status === "running";
                    return (
                      <li key={`${r.jobname}-${r.start_time}-${i}`} className="flex items-center gap-2 border-b border-border/30 py-1">
                        {isOk ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" /> : isRunning ? <Clock className="h-3.5 w-3.5 shrink-0 text-blue-500" /> : <XCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />}
                        <code className="shrink-0">{r.jobname.replace(/^refresh-route-prices-/, "off-")}</code>
                        <span className="shrink-0 text-muted-foreground">{new Date(r.start_time).toLocaleTimeString("pl")}</span>
                        {r.duration_s !== null && <span className="shrink-0 text-muted-foreground">· {r.duration_s}s</span>}
                        {!isOk && r.return_message && (
                          <span className="min-w-0 truncate text-red-600 dark:text-red-400" title={r.return_message}>{r.return_message}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}
